/**
 * TideWars — Realtime-Landing-Feed (Batch 0.6.0, Gate live-spectating).
 *
 * Kleines eigenständiges Landing-Script (KEIN Three.js): verbindet mit
 * `/ws/lobby` (→ region-gepinnter LobbyFeedDO) und re-rendert bei jedem
 * `feed`-Payload die Sektionen `section.lobby-list` (offene Häfen) und
 * `section.live-matches` („Jetzt live"). Progressive Enhancement: ohne JS bleibt
 * die SSR-Baseline korrekt; dieses Script hält sie nur ohne Reload frisch.
 *
 * Die gerenderte Struktur spiegelt exakt `src/views/landing.ts`
 * (`lobbyListSection` / `liveMatchesSection`) — bei Änderungen dort mitziehen.
 * Reconnect-Backoff wie `net.ts`.
 */
import { format, parseLocale, t } from '../../i18n/index';
import type { FeedPayload, PublicLobby, PublicMatch } from '../../lib/lobby-listing';

const lobbyList = document.querySelector<HTMLElement>('section.lobby-list');
const liveMatches = document.querySelector<HTMLElement>('section.live-matches');

// Nur aktiv werden, wenn mindestens eine Ziel-Sektion existiert (Lobby-Browser AN).
if (lobbyList || liveMatches) {
  const locale = parseLocale(document.documentElement.lang);

  const esc = (value: unknown): string =>
    String(value).replace(/[&<>"']/g, (c) =>
      c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
    );

  const lobbyListInner = (lobbies: readonly PublicLobby[]): string => {
    const heading = `<h2>${esc(t(locale, 'lobby.list_heading'))}</h2>`;
    if (lobbies.length === 0) {
      return `${heading}<p class="lobby-empty">${esc(t(locale, 'lobby.list_empty'))}</p>`;
    }
    const rows = lobbies
      .map(
        (l) => `<li class="lobby-row">
        <span class="lobby-code">${esc(l.roomCode)}</span>
        <span class="lobby-mode">${esc(t(locale, `mode.${l.mode}.badge`))}</span>
        <span class="lobby-diff">${esc(t(locale, `difficulty.${l.difficulty}.name`))}</span>
        <span class="lobby-count">${esc(l.playerCount)}/${esc(l.maxPlayers)}</span>
        <form class="lobby-join" method="post" action="/rooms/join">
          <input type="hidden" name="join_code" value="${esc(l.roomCode)}" />
          <button type="submit" class="play-button mini-join">${esc(t(locale, 'lobby.list_join'))}</button>
        </form>
      </li>`,
      )
      .join('');
    return `${heading}<ul class="lobby-rows">${rows}</ul>`;
  };

  const liveMatchesInner = (matches: readonly PublicMatch[]): string => {
    if (matches.length === 0) return '';
    const rows = matches
      .map(
        (m) => `<li class="live-row">
        <span class="lobby-code">${esc(m.roomCode)}</span>
        <span class="live-badge">${esc(t(locale, 'spectate.live_badge'))}</span>
        <span class="lobby-diff">${esc(t(locale, `difficulty.${m.difficulty}.name`))}</span>
        <span class="live-wave">${esc(format(t(locale, 'live.wave_progress'), { n: m.wave }))}</span>
        <span class="live-spectators">${esc(format(t(locale, 'live.spectator_count'), { n: m.spectatorCount }))}</span>
        <a class="play-button mini-join" href="/watch/${encodeURIComponent(m.roomCode)}">${esc(t(locale, 'live.watch'))}</a>
      </li>`,
      )
      .join('');
    return `<h2>${esc(t(locale, 'live.heading'))}</h2><ul class="live-rows">${rows}</ul>`;
  };

  const render = (payload: FeedPayload): void => {
    if (lobbyList) lobbyList.innerHTML = lobbyListInner(payload.lobbies ?? []);
    if (liveMatches) liveMatches.innerHTML = liveMatchesInner(payload.live ?? []);
  };

  let ws: WebSocket | null = null;
  let reconnectDelayMs = 500;

  const connect = (): void => {
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    ws = new WebSocket(`${proto}//${location.host}/ws/lobby`);

    ws.addEventListener('open', () => {
      reconnectDelayMs = 500;
    });

    ws.addEventListener('message', (event) => {
      let payload: FeedPayload;
      try {
        payload = JSON.parse(String(event.data)) as FeedPayload;
      } catch {
        return; // Server garantiert JSON
      }
      if (payload && payload.type === 'feed') render(payload);
    });

    ws.addEventListener('close', () => {
      setTimeout(connect, reconnectDelayMs);
      reconnectDelayMs = Math.min(reconnectDelayMs * 2, 10_000);
    });

    ws.addEventListener('error', () => {
      try {
        ws?.close();
      } catch {
        /* bereits zu */
      }
    });
  };

  connect();
}
